import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  ChatBubbleBottomCenterTextIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const ChatContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  font-family: inherit;
`;

const ChatButton = styled.button<{ $isOpen: boolean }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
  border: none;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(11,152,206,0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(11,152,206,0.4);
  }
  
  ${props => !props.$isOpen && css`
    animation: ${pulse} 3s ease-in-out infinite;
  `}
  
  svg {
    width: 28px;
    height: 28px;
    transition: all 0.3s ease;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
    border-radius: 50%;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 0.3;
  }
`;

const ChatWindow = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  bottom: 80px;
  right: 0;
  width: 380px;
  height: 500px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(11,152,206,0.1);
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: ${props => props.$isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)'};
  opacity: ${props => props.$isOpen ? '1' : '0'};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  ${props => props.$isOpen ? css`animation: ${slideUp} 0.4s ease-out;` : ''}
  
  @media (max-width: 420px) {
    width: 95vw;
    right: -15px;
    height: 70vh;
    max-height: 500px;
  }
`;

const ChatHeader = styled.div`
  padding: var(--spacing-lg);
  background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  h3 {
    margin: 0;
    font-size: var(--fs-md);
    font-weight: var(--font-weight-bold);
    color: white !important;
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    
    svg {
      width: 20px;
      height: 20px;
      color: white;
    }
  }
  
  button {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: var(--spacing-md);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(11,152,206,0.3);
    border-radius: 3px;
  }
`;

const Message = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  
  .message-content {
    max-width: 80%;
    padding: var(--spacing-sm) var(--spacing-md);
    border-radius: ${props => props.$isUser 
      ? '16px 16px 4px 16px' 
      : '16px 16px 16px 4px'};
    background: ${props => props.$isUser 
      ? 'linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary))' 
      : 'var(--color-primary-light)'};
    color: ${props => props.$isUser ? 'white' : 'var(--text-primary)'};
    font-size: var(--fs-sm);
    line-height: 1.4;
    word-wrap: break-word;
    
    a {
      color: ${props => props.$isUser ? 'rgba(255,255,255,0.9)' : 'var(--color-accent-primary)'};
      text-decoration: underline;
      
      &:hover {
        text-decoration: none;
      }
    }
  }
  
  .message-time {
    font-size: var(--fs-xs);
    color: var(--text-tertiary);
    margin-top: var(--spacing-xs);
    padding: 0 var(--spacing-sm);
  }
`;

const ChatInput = styled.div`
  padding: var(--spacing-md);
  border-top: 1px solid rgba(11,152,206,0.1);
  display: flex;
  gap: var(--spacing-sm);
  
  input {
    flex: 1;
    padding: var(--spacing-sm) var(--spacing-md);
    border: 1px solid rgba(11,152,206,0.2);
    border-radius: 20px;
    background: white;
    font-size: var(--fs-sm);
    outline: none;
    transition: all 0.2s ease;
    
    &:focus {
      border-color: var(--color-accent-primary);
      box-shadow: 0 0 0 3px rgba(11,152,206,0.1);
    }
    
    &::placeholder {
      color: #9ca3af;
      opacity: 1;
    }
  }
  
  button {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(11,152,206,0.3);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-primary-light);
  border-radius: 16px 16px 16px 4px;
  max-width: 80%;
  font-size: var(--fs-sm);
  color: var(--text-secondary);
  
  .dots {
    display: flex;
    gap: 2px;
    
    span {
      width: 4px;
      height: 4px;
      background: var(--color-accent-primary);
      border-radius: 50%;
      ${css`animation: ${pulse} 1.5s ease-in-out infinite;`}
      
      &:nth-child(2) { animation-delay: 0.2s; }
      &:nth-child(3) { animation-delay: 0.4s; }
    }
  }
`;

const AIChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: "Hi! I'm Leo, here to help with Davinci questions. What's up?",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputValue,
          history: messages.slice(-5) // Send last 5 messages for context
        }),
      });

      const data = await response.json();

      if (data.success) {
        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: data.response,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiMessage]);
      } else {
        throw new Error(data.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "I'm sorry, I'm having trouble connecting right now. Please feel free to contact us directly at (844) 833-8246 or email info@davincisolutions.ai",
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <ChatContainer>
      <ChatWindow $isOpen={isOpen}>
        <ChatHeader>
          <h3>
            <SparklesIcon />
            Leo - AI Assistant
          </h3>
          <button onClick={() => setIsOpen(false)}>
            <XMarkIcon />
          </button>
        </ChatHeader>
        
        <ChatMessages>
          {messages.map((message) => (
            <Message key={message.id} $isUser={message.isUser}>
              <div className="message-content">{message.content}</div>
              <div className="message-time">{formatTime(message.timestamp)}</div>
            </Message>
          ))}
          
          {isLoading && (
            <Message $isUser={false}>
              <TypingIndicator>
                Leo is typing
                <div className="dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </TypingIndicator>
            </Message>
          )}
          
          <div ref={messagesEndRef} />
        </ChatMessages>
        
        <ChatInput>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me about Davinci."
            disabled={isLoading}
          />
          <button onClick={handleSendMessage} disabled={isLoading || !inputValue.trim()}>
            <PaperAirplaneIcon />
          </button>
        </ChatInput>
      </ChatWindow>
      
      <ChatButton $isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <XMarkIcon /> : <ChatBubbleBottomCenterTextIcon />}
      </ChatButton>
    </ChatContainer>
  );
};

export default AIChat;